import { isEmpty } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import {
  SideNavStateProvider,
  SideNav as OrcsSideNav,
  Badge,
} from "orcs-design-system";

import useSideNavClickListener from "src/hooks/useSideNavClickListener";
import {
  useWorkspaceTagConfig,
  useGroupTypes,
  useWorkspaceFeatureFlags,
} from "src/contexts/global/WorkspaceContext";
import { useAuth0 } from "src/contexts/auth0Provider";
import { useFeatureFlags } from "src/contexts/global/FeatureFlagsContext";
import useSideNavItemBuilder from "src/sidebar/useSideNavItemBuilder";
import eventEmitter from "src/services/eventEmitter";
import { usePinnedEntities } from "src/hooks/usePinnedEntities";
import useUserTeams from "src/components/AppSideNavWrapper/useUserTeams";
import { useUserSideNav } from "src/hooks/userPreferences/hooks/useUserSideNav";
import { useAppHeadersHeightCalc } from "src/hooks/useAppHeadersHeightCalc";
import { getGroupTypeBadgeVariant } from "src/util/group";
import { getGroupType } from "src/util/groupType";
import GroupTypeBadge from "src/components/Badge/GroupTypeBadge";
import { useAISidebar } from "src/hooks/useAISidebar";
import useResizableChatPaneWidth from "src/components/AIContent/v3/hooks/useResizableChatPaneWidth";
import useFilterNavItems from "./hooks/useFilterNavItems";

const AppSideNavWrapper = () => {
  const [clickedItem, setClickedItem] = useState({});
  const tagTypesConfig = useWorkspaceTagConfig();
  const { user } = useAuth0();
  const featureFlags = useFeatureFlags();
  const { userTeams } = useUserTeams();
  const { pinnedEntities, reorderVisiblePinnedItems } = usePinnedEntities();
  const { sideNav, setKeepExpanded } = useUserSideNav();
  const { enableAIChat } = useWorkspaceFeatureFlags();
  const { isOpen: isChatOpen } = useAISidebar();
  const { smallScreenBreakpoint } = useResizableChatPaneWidth(
    Boolean(enableAIChat) && isChatOpen
  );
  const [isPinnedEditMode, setIsPinnedEditMode] = useState(false);
  const groupTypesRaw = useGroupTypes();
  const groupTypes = useMemo(() => groupTypesRaw || {}, [groupTypesRaw]);
  const sideNavHeight = useAppHeadersHeightCalc({
    includeProfileHeaders: false,
    hideHeader: false,
  });

  const navItems = useSideNavItemBuilder({
    clickedItem,
    user,
    tagTypesConfig,
    featureFlags,
    eventEmitter,
    setClickedItem,
  });

  useSideNavClickListener();
  const filteredNavItems = useFilterNavItems(navItems);

  const handleKeepExpandedChange = useCallback(
    (keepExpanded) => {
      // fire-and-forget preference persistence
      void setKeepExpanded(keepExpanded);
    },
    [setKeepExpanded]
  );

  // Enrich teams with the design-system SideNav fields (badgeVariant +
  // popoverBadge) so SideNavTeamsSection can render them natively without
  // a custom render-prop override.
  const enrichedTeams = useMemo(
    () =>
      (userTeams || []).map((team) => {
        const { type, id, name } = team;
        const hasTeamType = Boolean(type);
        const groupObject = { type, id, name };
        const groupType = hasTeamType
          ? getGroupType(groupObject, groupTypes)
          : null;
        const badgeVariant = groupType
          ? getGroupTypeBadgeVariant(groupType)
          : "default";

        return {
          ...team,
          badgeVariant,
          popoverBadge: hasTeamType ? (
            <GroupTypeBadge group={groupObject} groupTypes={groupTypes} />
          ) : null,
        };
      }),
    [userTeams, groupTypes]
  );

  const enrichedPinnedItems = useMemo(
    () =>
      (pinnedEntities || []).map((item) => {
        const { type, entityType, id, name, jobTitle, tagTypeName } = item;
        const showTeamType = entityType === "team" && Boolean(type);
        const groupObject = showTeamType ? { type, id, name } : null;
        const groupType = showTeamType
          ? getGroupType(groupObject, groupTypes)
          : null;
        const badgeVariant = groupType
          ? getGroupTypeBadgeVariant(groupType)
          : "default";

        let popoverBadge = null;
        if (showTeamType) {
          popoverBadge = (
            <GroupTypeBadge group={groupObject} groupTypes={groupTypes} />
          );
        } else if (entityType === "person" && jobTitle) {
          popoverBadge = <Badge>{jobTitle}</Badge>;
        } else if (entityType === "tag" && tagTypeName) {
          popoverBadge = <Badge>{tagTypeName}</Badge>;
        }

        return {
          ...item,
          badgeVariant,
          popoverBadge,
        };
      }),
    [pinnedEntities, groupTypes]
  );

  const handlePinnedItemsReorder = useCallback(
    (fromIndex, toIndex) => {
      reorderVisiblePinnedItems(fromIndex, toIndex);
    },
    [reorderVisiblePinnedItems]
  );

  if (isEmpty(filteredNavItems)) {
    return null;
  }

  return (
    <SideNavStateProvider smallScreenBreakpoint={smallScreenBreakpoint}>
      <OrcsSideNav
        items={filteredNavItems}
        sideNavHeight={sideNavHeight}
        yourTeams={enrichedTeams}
        pinnedItems={enrichedPinnedItems}
        onPinnedItemsReorder={handlePinnedItemsReorder}
        isPinnedEditMode={isPinnedEditMode}
        onPinnedItemsEditModeChange={setIsPinnedEditMode}
        startExpanded={sideNav?.keepExpanded}
        onKeepExpandedChange={handleKeepExpandedChange}
      />
    </SideNavStateProvider>
  );
};

export default AppSideNavWrapper;
